import {useIsVisualizationEnabled} from '@github-ui/issue-agent-tasks/utils/is-visualization-enabled'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {CommentIcon} from '@primer/octicons-react'
import {graphql, useFragment} from 'react-relay'

import {QUERY_FIELDS} from '../constants/queries'
import {TEST_IDS} from '../constants/test-ids'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import type {PullRequestItemMetadata$key} from './__generated__/PullRequestItemMetadata.graphql'
import {Assignees} from './assignees/Assignees'
import {LoadingMetadata} from './IssueItemMetadata'
import styles from './PullRequestItemMetadata.module.css'
import {Reactions} from './Reactions'
import {getReactionDisplayForSort, isReactionSort} from './sorting/SortingDropdown'

export type PullRequestItemMetadataProps = {
  itemKey: PullRequestItemMetadata$key
  showAssignees?: boolean
  showCommentCount?: boolean
  showCommentZeroCount?: boolean
  /**
   * Href getter for the metadata badge links
   * @param type - metadata type
   * @param name - name of the metadata
   * @returns URL to the metadata
   */
  getMetadataHref: (type: keyof typeof QUERY_FIELDS, metadataName: string) => string
  sortingItemSelected?: string
}

export type LazyPullItemMetadataProps = Omit<PullRequestItemMetadataProps, 'itemKey'> & {
  pullId: string
  listItemMetadata?: IssueRowSecondaryQuery$data | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PullRequestItemMetadataQuery = graphql`
  fragment PullRequestItemMetadata on PullRequest
  @argumentDefinitions(
    assigneePageSize: {type: "Int!", defaultValue: 10}
    includeReactions: {type: "Boolean!", defaultValue: false}
  ) {
    totalCommentsCount
    # Used to check if reactions were loaded
    reactionGroups @include(if: $includeReactions) {
      __typename
    }
    ... on Reactable @include(if: $includeReactions) {
      ...Reactions @dangerously_unaliased_fixme @include(if: $includeReactions)
    }
    ...Assignees @arguments(assigneePageSize: $assigneePageSize)
  }
`
export const LazyPullRequestItemMetadata = ({listItemMetadata, pullId, ...props}: LazyPullItemMetadataProps) => {
  // We need this to render the loading state in ssr
  if (!listItemMetadata) return <LoadingMetadata {...props} />

  const pullNode = listItemMetadata.nodes?.find(node => node?.id === pullId)

  if (!pullNode) return null

  return <PullRequestItemMetadata itemKey={pullNode} {...props} />
}

export const PullRequestItemMetadata = ({
  itemKey,
  getMetadataHref,
  sortingItemSelected,
  showAssignees = true,
  showCommentCount = true,
  showCommentZeroCount = false,
}: PullRequestItemMetadataProps) => {
  const {variant} = useListViewVariant()
  const data = useFragment(PullRequestItemMetadataQuery, itemKey)
  const visualizationEnabled = useIsVisualizationEnabled()

  const commentAriaLabel =
    data?.totalCommentsCount && data.totalCommentsCount > 0
      ? ` ${data.totalCommentsCount} comment${data.totalCommentsCount > 1 ? 's' : ''};`
      : ''
  const showReactions = isReactionSort(sortingItemSelected || '')
  const reactionEmojiToDisplay = getReactionDisplayForSort(sortingItemSelected || '')
  const showCommentCountMetadata =
    (showCommentCount && !!data.totalCommentsCount) || (showCommentCount && showCommentZeroCount)

  return (
    <>
      <ListItemMetadata
        aria-label={commentAriaLabel}
        data-testid={TEST_IDS.listRowComments}
        className={styles.ListItemMetadata_0}
      >
        {showCommentCountMetadata && (
          <>
            <CommentIcon size={16} /> {data.totalCommentsCount ? data.totalCommentsCount : 0}
          </>
        )}
      </ListItemMetadata>
      {showReactions && (
        <ListItemMetadata className={styles.ListItemMetadata_0}>
          {data.reactionGroups && (
            <Reactions
              dataKey={data}
              reactionEmojiToDisplay={reactionEmojiToDisplay}
              showCompactDensity={variant === 'compact'}
            />
          )}
        </ListItemMetadata>
      )}
      {showAssignees && data && visualizationEnabled && (
        <ListItemMetadata
          data-testid={TEST_IDS.listRowAssignedAgents}
          alignment="right"
          className={styles.ListItemMetadata_0}
        >
          <Assignees
            assigneeskey={data}
            filter="agents"
            getAssigneeHref={assignee => getMetadataHref(QUERY_FIELDS.assignee, assignee)}
          />
        </ListItemMetadata>
      )}
      <ListItemMetadata data-testid={TEST_IDS.listRowAssignees} alignment="right" className={styles.ListItemMetadata_0}>
        {showAssignees && data && (
          <Assignees
            assigneeskey={data}
            filter={visualizationEnabled ? 'users' : undefined}
            getAssigneeHref={assignee => getMetadataHref(QUERY_FIELDS.assignee, assignee)}
          />
        )}
      </ListItemMetadata>
    </>
  )
}
